/**
 * APK Settings Component v2.0 - MASSIVELY IMPROVED
 * 
 * Premium settings panel for APK users with:
 * ✅ Beautiful modern UI with animations
 * ✅ Biometric enrollment and management
 * ✅ Performance optimization controls
 * ✅ Notification management
 * ✅ Offline/Data settings
 * ✅ Security features
 * ✅ Gesture controls
 * ✅ Storage management
 * ✅ Account settings
 * ✅ Advanced debugging
 */

import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
    Settings, X, Zap, Bell, Wifi, WifiOff, Vibrate, Battery,
    HardDrive, Trash2, Check, ChevronRight, Moon, Sun,
    Gauge, RefreshCw, Download, Shield, Clock, Volume2,
    Smartphone, AlertCircle, Info, Sparkles, Eye, BellRing,
    MessageSquare, Trophy, Gift, Timer, Save, Fingerprint,
    User, LogOut, Lock, Palette, Database, Server, Globe,
    ChevronDown, ChevronUp, Loader2, CheckCircle, XCircle,
    Waves, Brain, BookOpen, Star, Cpu, Activity, MemoryStick
} from 'lucide-react';
import { nativeFeatures } from '../services/nativeFeaturesService';
import {
    isBiometricAvailable,
    registerBiometric,
    getBiometricType,
    getBiometricCredentials,
    removeBiometricCredential,
    isBiometricSupportedPlatform,
    clearBiometricData,
    BiometricCredential
} from '../utils/biometricAuth';
import { User as UserType } from '../types';

// Settings types
export interface APKSettingsConfig {
    // Performance
    targetFPS: 30 | 60 | 120;
    enableHardwareAcceleration: boolean;
    reducedAnimations: boolean;
    enableGPURendering: boolean;
    enableMemoryOptimization: boolean;

    // Notifications
    enableNotifications: boolean;
    notifyOnNewResponse: boolean;
    notifyOnTokenRefresh: boolean;
    notifyOnAchievements: boolean;
    notifyOnQuests: boolean;
    studyReminderEnabled: boolean;
    studyReminderTime: string;
    notificationSound: boolean;

    // Haptics
    enableHaptics: boolean;
    hapticIntensity: 'light' | 'medium' | 'heavy';

    // Offline & Data
    enableOfflineMode: boolean;
    autoDownloadContent: boolean;
    dataSaverMode: boolean;
    maxCacheSize: number;

    // Battery
    batteryOptimization: boolean;
    lowPowerMode: boolean;
    autoSleepDelay: number;

    // Theme
    autoTheme: boolean;
    autoThemeStartTime: string;
    autoThemeEndTime: string;

    // Security & Quick Access
    biometricQuickAccess: boolean;
    lockAppOnBackground: boolean;
    autoLockDelay: number;

    // Gestures & Navigation
    enableSwipeGestures: boolean;
    doubleTapToTop: boolean;
    shakeToRefresh: boolean;

    // Advanced
    debugMode: boolean;
    showFPSCounter: boolean;
    autoClearCache: boolean;
    clearCacheInterval: number;
    networkOptimization: boolean;
    preloadImages: boolean;

    // AI Settings
    streamingEnabled: boolean;
    autoSaveConversations: boolean;
}

const DEFAULT_APK_SETTINGS: APKSettingsConfig = {
    targetFPS: 60,
    enableHardwareAcceleration: true,
    reducedAnimations: false,
    enableGPURendering: true,
    enableMemoryOptimization: true,
    enableNotifications: true,
    notifyOnNewResponse: true,
    notifyOnTokenRefresh: true,
    notifyOnAchievements: true,
    notifyOnQuests: true,
    studyReminderEnabled: false,
    studyReminderTime: '09:00',
    notificationSound: true,
    enableHaptics: true,
    hapticIntensity: 'medium',
    enableOfflineMode: true,
    autoDownloadContent: false,
    dataSaverMode: false,
    maxCacheSize: 500,
    batteryOptimization: false,
    lowPowerMode: false,
    autoSleepDelay: 30,
    autoTheme: true,
    autoThemeStartTime: '18:00',
    autoThemeEndTime: '06:00',
    biometricQuickAccess: false,
    lockAppOnBackground: false,
    autoLockDelay: 5,
    enableSwipeGestures: true,
    doubleTapToTop: true,
    shakeToRefresh: false,
    debugMode: false,
    showFPSCounter: false,
    autoClearCache: false,
    clearCacheInterval: 7,
    networkOptimization: true,
    preloadImages: true,
    streamingEnabled: true,
    autoSaveConversations: true,
};

interface APKSettingsProps {
    isOpen: boolean;
    onClose: () => void;
    isDarkMode: boolean;
    onThemeToggle?: () => void;
    user?: UserType | null;
    onLogout?: () => void;
    showToast?: (type: 'success' | 'error' | 'warning' | 'info', message: string) => void;
}

// Enhanced Setting Toggle Component
const SettingToggle: React.FC<{
    icon: React.ReactNode;
    title: string;
    description: string;
    enabled: boolean;
    onChange: (enabled: boolean) => void;
    color?: string;
    disabled?: boolean;
    premium?: boolean;
}> = ({ icon, title, description, enabled, onChange, color = 'indigo', disabled = false, premium = false }) => (
    <div className={`
        flex items-center justify-between p-4 rounded-2xl transition-all duration-300
        ${disabled ? 'opacity-50 pointer-events-none' : ''}
        ${enabled
            ? `bg-gradient-to-r from-${color}-500/10 to-${color}-500/5 border border-${color}-500/30 shadow-lg shadow-${color}-500/5`
            : 'bg-slate-800/50 border border-slate-700/50 hover:border-slate-600/50'
        }
    `}>
        <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`
                w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-300
                ${enabled
                    ? `bg-gradient-to-br from-${color}-500 to-${color}-600 text-white shadow-lg shadow-${color}-500/30`
                    : 'bg-slate-700/80 text-slate-400'
                }
            `}>
                {icon}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <p className="font-semibold text-white text-sm leading-tight">{title}</p>
                    {premium && (
                        <span className="px-1.5 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-[9px] font-bold text-white rounded uppercase flex-shrink-0">
                            PRO
                        </span>
                    )}
                </div>
                <p className="text-xs text-slate-400 leading-tight mt-0.5">{description}</p>
            </div>
        </div>
        <button
            onClick={() => !disabled && onChange(!enabled)}
            disabled={disabled}
            className={`
                relative w-14 h-8 rounded-full transition-all duration-300 flex-shrink-0 ml-3
                ${enabled
                    ? `bg-gradient-to-r from-${color}-500 to-${color}-400 shadow-lg shadow-${color}-500/30`
                    : 'bg-slate-600/80'
                }
            `}
        >
            <span className={`
                absolute top-1 w-6 h-6 bg-white rounded-full shadow-lg transition-all duration-300 ease-out
                ${enabled ? 'translate-x-7' : 'translate-x-1'}
            `}>
                {enabled && (
                    <Check size={14} className="absolute inset-0 m-auto text-emerald-500" />
                )}
            </span>
        </button>
    </div>
);

// Enhanced Setting Select Component
const SettingSelect: React.FC<{
    icon: React.ReactNode;
    title: string;
    description: string;
    value: string | number;
    options: { value: string | number; label: string; icon?: React.ReactNode }[];
    onChange: (value: any) => void;
    color?: string;
}> = ({ icon, title, description, value, options, onChange, color = 'indigo' }) => (
    <div className="p-4 rounded-2xl bg-slate-800/50 border border-slate-700/50">
        <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-slate-700/80 text-slate-400">
                {icon}
            </div>
            <div className="flex-1">
                <p className="font-semibold text-white text-sm">{title}</p>
                <p className="text-xs text-slate-400">{description}</p>
            </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
            {options.map((opt) => (
                <button
                    key={opt.value}
                    onClick={() => onChange(opt.value)}
                    className={`
                        py-2.5 px-3 rounded-xl text-xs font-medium transition-all duration-300 flex items-center justify-center gap-1.5
                        ${value === opt.value
                            ? `bg-gradient-to-r from-${color}-500 to-${color}-400 text-white shadow-lg shadow-${color}-500/30`
                            : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700 hover:text-white'
                        }
                    `}
                >
                    {opt.icon}
                    {opt.label}
                </button>
            ))}
        </div>
    </div>
);

// Section Header Component
const SectionHeader: React.FC<{
    icon: React.ReactNode;
    title: string;
    description?: string;
    color?: string;
}> = ({ icon, title, description, color = 'slate' }) => (
    <div className="flex items-center gap-3 mb-4 mt-6 first:mt-0">
        <div className={`w-8 h-8 rounded-lg bg-${color}-500/20 flex items-center justify-center`}>
            {React.cloneElement(icon as any, { size: 16, className: `text-${color}-400` })}
        </div>
        <div>
            <h3 className="text-sm font-bold text-white">{title}</h3>
            {description && <p className="text-xs text-slate-500">{description}</p>}
        </div>
    </div>
);

// Biometric Section Component
const BiometricSection: React.FC<{
    user: UserType | null;
    isDarkMode: boolean;
    showToast?: (type: 'success' | 'error' | 'warning' | 'info', message: string) => void;
    onBiometricChange?: (enabled: boolean) => void;
}> = ({ user, isDarkMode, showToast, onBiometricChange }) => {
    const [isAvailable, setIsAvailable] = useState(false);
    const [isSupported, setIsSupported] = useState(false);
    const [isChecking, setIsChecking] = useState(true);
    const [isEnrolling, setIsEnrolling] = useState(false);
    const [credentials, setCredentials] = useState<BiometricCredential[]>([]);
    const [biometricType, setBiometricType] = useState('Biometric');
    const [error, setError] = useState<string | null>(null);
    const [showDevices, setShowDevices] = useState(false);

    useEffect(() => {
        checkBiometric();
    }, [user?.id]);

    const checkBiometric = async () => {
        setIsChecking(true);
        try {
            const supported = await isBiometricSupportedPlatform();
            setIsSupported(supported);

            if (supported) {
                const available = await isBiometricAvailable();
                setIsAvailable(available);
                setBiometricType(getBiometricType());

                if (user?.id) {
                    const creds = getBiometricCredentials(user.id);
                    setCredentials(creds);
                }
            }
        } catch (e) {
            console.error('[BiometricSection] Check error:', e);
        } finally {
            setIsChecking(false);
        }
    };

    const handleEnroll = async () => {
        if (!user) {
            showToast?.('error', 'Please log in first to set up biometrics');
            return;
        }

        setIsEnrolling(true);
        setError(null);

        try {
            const result = await registerBiometric(
                user.id,
                user.email || user.username,
                user.username
            );

            if (result.success) {
                showToast?.('success', `✅ ${biometricType} set up successfully!`);
                onBiometricChange?.(true);
                await checkBiometric();
            } else {
                setError(result.error || 'Failed to set up biometric');
                showToast?.('error', result.error || 'Setup failed');
            }
        } catch (e: any) {
            setError(e.message || 'An error occurred');
            showToast?.('error', 'An error occurred during setup');
        } finally {
            setIsEnrolling(false);
        }
    };

    const handleRemoveCredential = async (credId: string) => {
        if (!user) return;

        const success = removeBiometricCredential(user.id, credId);
        if (success) {
            showToast?.('success', 'Device removed');
            await checkBiometric();
            if (credentials.length <= 1) {
                onBiometricChange?.(false);
            }
        } else {
            showToast?.('error', 'Failed to remove device');
        }
    };

    const handleClearAll = async () => {
        if (!user) return;

        clearBiometricData(user.id);
        showToast?.('success', 'All biometric data cleared');
        onBiometricChange?.(false);
        await checkBiometric();
    };

    if (isChecking) {
        return (
            <div className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700/50 flex items-center justify-center">
                <Loader2 size={24} className="animate-spin text-purple-500" />
            </div>
        );
    }

    if (!isSupported) {
        return (
            <div className="p-4 rounded-2xl bg-blue-500/10 border border-blue-500/30">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                        <Smartphone size={20} className="text-blue-400" />
                    </div>
                    <div>
                        <p className="text-sm font-semibold text-blue-400 mb-1">Not Available</p>
                        <p className="text-xs text-blue-300/80 leading-relaxed">
                            Biometric authentication requires the APK version on Android or Windows with Windows Hello.
                            Use the installed app for biometric login.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const isEnrolled = credentials.length > 0;

    return (
        <div className="space-y-3">
            {/* Status Card */}
            <div className={`p-4 rounded-2xl border transition-all duration-300 ${isEnrolled
                ? 'bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border-emerald-500/30'
                : 'bg-gradient-to-br from-indigo-500/10 to-violet-500/5 border-indigo-500/30'
                }`}>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isEnrolled
                            ? 'bg-gradient-to-br from-emerald-500 to-teal-500'
                            : 'bg-gradient-to-br from-indigo-600 to-violet-600'
                            } shadow-lg`}>
                            <Fingerprint size={24} className="text-white" />
                        </div>
                        <div>
                            <p className="font-bold text-white">{biometricType}</p>
                            <p className="text-xs text-slate-400">
                                {isEnrolled ? `${credentials.length} device(s) enrolled` : 'Not set up'}
                            </p>
                        </div>
                    </div>
                    <div className={`px-3 py-1.5 rounded-full text-xs font-bold ${isEnrolled
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'bg-indigo-500/20 text-indigo-400'
                        }`}>
                        {isEnrolled ? 'Active' : 'Inactive'}
                    </div>
                </div>

                {/* Enroll Button or Manage Devices */}
                {isEnrolled ? (
                    <div className="space-y-2">
                        <button
                            onClick={() => setShowDevices(!showDevices)}
                            className="w-full py-2.5 px-4 rounded-xl bg-slate-700/50 hover:bg-slate-700 text-white text-sm font-medium flex items-center justify-between transition-all"
                        >
                            <span className="flex items-center gap-2">
                                <Smartphone size={16} />
                                Manage Devices
                            </span>
                            {showDevices ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>

                        {showDevices && (
                            <div className="space-y-2 animate-in slide-in-from-top-2 duration-200">
                                {credentials.map((cred) => (
                                    <div key={cred.id} className="flex items-center justify-between p-3 rounded-xl bg-slate-800/80 border border-slate-700/50">
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-lg bg-purple-500/20 flex items-center justify-center">
                                                <Smartphone size={16} className="text-purple-400" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-white">{cred.deviceName}</p>
                                                <p className="text-[10px] text-slate-500">
                                                    Added {new Date(cred.createdAt).toLocaleDateString()}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleRemoveCredential(cred.id)}
                                            className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                ))}

                                <button
                                    onClick={handleEnroll}
                                    disabled={isEnrolling}
                                    className="w-full py-2.5 rounded-xl bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 text-sm font-medium transition-all disabled:opacity-50"
                                >
                                    {isEnrolling ? <Loader2 size={16} className="animate-spin mx-auto" /> : '+ Add Another Device'}
                                </button>

                                <button
                                    onClick={handleClearAll}
                                    className="w-full py-2 text-xs text-red-400 hover:text-red-300 transition-colors"
                                >
                                    Clear all biometric data
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <button
                        onClick={handleEnroll}
                        disabled={isEnrolling || !user}
                        className={`
                            w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2
                            ${isEnrolling
                                ? 'bg-slate-700 text-slate-400'
                                : 'bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white shadow-lg shadow-indigo-500/25 active:scale-[0.98]'
                            }
                            disabled:opacity-50 disabled:cursor-not-allowed
                        `}
                    >
                        {isEnrolling ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                Setting up...
                            </>
                        ) : (
                            <>
                                <Shield size={18} />
                                Enable {biometricType}
                            </>
                        )}
                    </button>
                )}

                {error && (
                    <div className="mt-3 p-3 rounded-xl bg-red-500/10 border border-red-500/30">
                        <p className="text-xs text-red-400">{error}</p>
                    </div>
                )}
            </div>

            {/* Benefits */}
            <div className="p-3 rounded-xl bg-slate-800/30 border border-slate-700/30">
                <p className="text-xs text-slate-500 mb-2 font-medium">Why use biometrics?</p>
                <div className="grid grid-cols-2 gap-2">
                    {[
                        { icon: <Zap size={12} />, text: 'Instant login' },
                        { icon: <Shield size={12} />, text: 'Enhanced security' },
                        { icon: <Lock size={12} />, text: 'Data stays on device' },
                        { icon: <WifiOff size={12} />, text: 'Works offline' },
                    ].map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-slate-400">
                            {item.icon}
                            <span className="text-[11px]">{item.text}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div >
    );
};

const APKSettings: React.FC<APKSettingsProps> = ({
    isOpen,
    onClose,
    isDarkMode,
    onThemeToggle,
    user,
    onLogout,
    showToast
}) => {
    const [settings, setSettings] = useState<APKSettingsConfig>(DEFAULT_APK_SETTINGS);
    const [activeTab, setActiveTab] = useState<'general' | 'biometric' | 'performance' | 'notifications' | 'storage' | 'advanced'>('general');
    const [isSaving, setIsSaving] = useState(false);
    const [showSaved, setShowSaved] = useState(false);
    const [cacheSize, setCacheSize] = useState<string>('Calculating...');
    const [memoryUsage, setMemoryUsage] = useState<string>('--');
    const [storageUsage, setStorageUsage] = useState<{ used: number; total: number }>({ used: 0, total: 0 });

    // Load settings on mount
    useEffect(() => {
        const savedSettings = localStorage.getItem('luca_apk_settings');
        if (savedSettings) {
            try {
                setSettings({ ...DEFAULT_APK_SETTINGS, ...JSON.parse(savedSettings) });
            } catch (e) {
                console.error('[APKSettings] Failed to parse settings');
            }
        }

        calculateStorageInfo();
    }, []);

    // Apply performance settings
    useEffect(() => {
        if (typeof window === 'undefined') return;

        document.documentElement.style.setProperty(
            '--animation-duration-multiplier',
            settings.targetFPS === 30 ? '2' : settings.targetFPS === 60 ? '1' : '0.5'
        );

        if (settings.reducedAnimations) {
            document.documentElement.classList.add('reduce-motion');
        } else {
            document.documentElement.classList.remove('reduce-motion');
        }

        if (settings.enableGPURendering) {
            document.documentElement.classList.add('gpu-accelerated');
        } else {
            document.documentElement.classList.remove('gpu-accelerated');
        }

        if (settings.showFPSCounter) {
            showFPSDisplay();
        } else {
            hideFPSDisplay();
        }
    }, [settings.targetFPS, settings.reducedAnimations, settings.enableGPURendering, settings.showFPSCounter]);

    const calculateStorageInfo = async () => {
        try {
            if ('storage' in navigator && 'estimate' in navigator.storage) {
                const estimate = await navigator.storage.estimate();
                const usedMB = Math.round((estimate.usage || 0) / 1024 / 1024);
                const totalMB = Math.round((estimate.quota || 0) / 1024 / 1024);
                setCacheSize(`${usedMB} MB`);
                setStorageUsage({ used: usedMB, total: totalMB });
            }

            // Memory info (Chrome only)
            if ((performance as any).memory) {
                const memory = (performance as any).memory;
                const usedMB = Math.round(memory.usedJSHeapSize / 1024 / 1024);
                setMemoryUsage(`${usedMB} MB`);
            }
        } catch (e) {
            setCacheSize('N/A');
        }
    };

    const showFPSDisplay = () => {
        let fpsElement = document.getElementById('fps-counter');
        if (!fpsElement) {
            fpsElement = document.createElement('div');
            fpsElement.id = 'fps-counter';
            fpsElement.style.cssText = `
                position: fixed;
                top: 60px;
                right: 10px;
                background: rgba(0,0,0,0.9);
                color: #10b981;
                padding: 6px 12px;
                border-radius: 8px;
                font-family: monospace;
                font-size: 12px;
                z-index: 99999;
                pointer-events: none;
                border: 1px solid rgba(16, 185, 129, 0.3);
            `;
            document.body.appendChild(fpsElement);
        }

        let lastTime = performance.now();
        let frameCount = 0;

        const updateFPS = () => {
            frameCount++;
            const now = performance.now();
            if (now - lastTime >= 1000) {
                if (fpsElement) {
                    fpsElement.textContent = `${frameCount} FPS`;
                    fpsElement.style.color = frameCount >= 55 ? '#10b981' : frameCount >= 30 ? '#f59e0b' : '#ef4444';
                }
                frameCount = 0;
                lastTime = now;
            }
            if (document.getElementById('fps-counter')) {
                requestAnimationFrame(updateFPS);
            }
        };
        requestAnimationFrame(updateFPS);
    };

    const hideFPSDisplay = () => {
        document.getElementById('fps-counter')?.remove();
    };

    const updateSetting = useCallback(<K extends keyof APKSettingsConfig>(
        key: K,
        value: APKSettingsConfig[K]
    ) => {
        setSettings(prev => {
            const newSettings = { ...prev, [key]: value };

            if (prev.enableHaptics) {
                nativeFeatures.hapticFeedback('light');
            }

            return newSettings;
        });
    }, []);

    const handleSave = async () => {
        setIsSaving(true);

        localStorage.setItem('luca_apk_settings', JSON.stringify(settings));

        if (settings.studyReminderEnabled && settings.studyReminderTime) {
            const [hours, minutes] = settings.studyReminderTime.split(':').map(Number);
            await nativeFeatures.scheduleStudyReminder(hours, minutes);
        }

        if (settings.enableHaptics) {
            await nativeFeatures.hapticFeedback('success');
        }

        setIsSaving(false);
        setShowSaved(true);
        showToast?.('success', 'Settings saved!');
        setTimeout(() => setShowSaved(false), 2000);
    };

    const handleClearCache = async () => {
        if (!confirm('Clear all cached data? This may affect offline functionality.')) return;

        try {
            if ('caches' in window) {
                const cacheNames = await caches.keys();
                await Promise.all(cacheNames.map(name => caches.delete(name)));
            }

            localStorage.removeItem('luca_offline_sessions');
            localStorage.removeItem('luca_cached_content');

            await calculateStorageInfo();
            showToast?.('success', 'Cache cleared successfully');

            if (settings.enableHaptics) {
                await nativeFeatures.hapticFeedback('success');
            }
        } catch (e) {
            showToast?.('error', 'Failed to clear cache');
        }
    };

    if (!isOpen) return null;

    const tabs = [
        { id: 'general', label: 'General', icon: <Settings size={16} /> },
        { id: 'biometric', label: 'Security', icon: <Fingerprint size={16} /> },
        { id: 'performance', label: 'Performance', icon: <Zap size={16} /> },
        { id: 'notifications', label: 'Alerts', icon: <Bell size={16} /> },
        { id: 'storage', label: 'Storage', icon: <Database size={16} /> },
        { id: 'advanced', label: 'Advanced', icon: <Cpu size={16} /> },
    ];

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-200 p-0 sm:p-4">
            <div className="relative w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-2xl bg-slate-900 sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">

                {/* Header */}
                <div className="flex-shrink-0 px-6 py-5 bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
                                <Settings className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h2 className="font-black text-white text-xl">Settings</h2>
                                <p className="text-xs text-white/70 font-medium">APK Exclusive Features</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2.5 rounded-xl bg-white/20 text-white hover:bg-white/30 transition-all active:scale-95"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* User Info */}
                    {user && (
                        <div className="mt-4 flex items-center justify-between p-3 rounded-xl bg-white/10 backdrop-blur-sm">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white font-bold">
                                    {user.username?.[0]?.toUpperCase() || 'U'}
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-white">{user.username}</p>
                                    <p className="text-xs text-white/60">{user.email}</p>
                                </div>
                            </div>
                            {user.isPremium && (
                                <span className="px-2 py-1 bg-gradient-to-r from-amber-400 to-yellow-300 text-amber-900 text-[10px] font-bold rounded-full uppercase shadow-sm">
                                    Premium
                                </span>
                            )}
                        </div>
                    )}

                    {/* Tabs */}
                    <div className="flex gap-1 mt-4 bg-white/10 rounded-xl p-1 overflow-x-auto scrollbar-none">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as any)}
                                className={`
                                    flex-shrink-0 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-lg text-xs font-semibold transition-all
                                    ${activeTab === tab.id
                                        ? 'bg-white text-indigo-600 shadow-lg'
                                        : 'text-white/80 hover:bg-white/10'
                                    }
                                `}
                            >
                                {tab.icon}
                                <span className="hidden sm:inline">{tab.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto space-y-4" style={{ maxHeight: 'calc(100dvh - 280px)' }}>

                    {/* General Tab */}
                    {activeTab === 'general' && (
                        <>
                            <SectionHeader icon={<Palette />} title="Appearance" color="purple" />

                            <SettingToggle
                                icon={isDarkMode ? <Moon size={18} /> : <Sun size={18} />}
                                title="Auto Theme"
                                description="Switch theme based on time of day"
                                enabled={settings.autoTheme}
                                onChange={(v) => updateSetting('autoTheme', v)}
                                color="purple"
                            />

                            <SettingToggle
                                icon={<Vibrate size={18} />}
                                title="Haptic Feedback"
                                description="Vibration on interactions"
                                enabled={settings.enableHaptics}
                                onChange={(v) => updateSetting('enableHaptics', v)}
                                color="purple"
                            />

                            {settings.enableHaptics && (
                                <SettingSelect
                                    icon={<Waves size={18} />}
                                    title="Haptic Intensity"
                                    description="How strong the vibrations feel"
                                    value={settings.hapticIntensity}
                                    options={[
                                        { value: 'light', label: 'Light' },
                                        { value: 'medium', label: 'Medium' },
                                        { value: 'heavy', label: 'Heavy' },
                                    ]}
                                    onChange={(v) => updateSetting('hapticIntensity', v)}
                                    color="purple"
                                />
                            )}

                            <SectionHeader icon={<Smartphone />} title="Gestures" color="blue" />

                            <SettingToggle
                                icon={<Smartphone size={18} />}
                                title="Swipe Gestures"
                                description="Navigate with swipe gestures"
                                enabled={settings.enableSwipeGestures}
                                onChange={(v) => updateSetting('enableSwipeGestures', v)}
                                color="blue"
                            />

                            <SettingToggle
                                icon={<Zap size={18} />}
                                title="Double Tap to Top"
                                description="Double tap to scroll to top"
                                enabled={settings.doubleTapToTop}
                                onChange={(v) => updateSetting('doubleTapToTop', v)}
                                color="blue"
                            />

                            <SettingToggle
                                icon={<RefreshCw size={18} />}
                                title="Shake to Refresh"
                                description="Shake device to refresh content"
                                enabled={settings.shakeToRefresh}
                                onChange={(v) => updateSetting('shakeToRefresh', v)}
                                color="blue"
                            />
                        </>
                    )}

                    {/* Biometric/Security Tab */}
                    {activeTab === 'biometric' && (
                        <>
                            <SectionHeader icon={<Fingerprint />} title="Biometric Login" color="purple" />

                            <BiometricSection
                                user={user || null}
                                isDarkMode={isDarkMode}
                                showToast={showToast}
                                onBiometricChange={(enabled) => updateSetting('biometricQuickAccess', enabled)}
                            />

                            <SectionHeader icon={<Lock />} title="App Lock" color="amber" />

                            <SettingToggle
                                icon={<Lock size={18} />}
                                title="Lock on Background"
                                description="Require authentication when returning"
                                enabled={settings.lockAppOnBackground}
                                onChange={(v) => updateSetting('lockAppOnBackground', v)}
                                color="amber"
                            />

                            {settings.lockAppOnBackground && (
                                <SettingSelect
                                    icon={<Timer size={18} />}
                                    title="Auto-Lock Delay"
                                    description="Time before app locks"
                                    value={settings.autoLockDelay}
                                    options={[
                                        { value: 0, label: 'Instant' },
                                        { value: 1, label: '1 min' },
                                        { value: 5, label: '5 min' },
                                    ]}
                                    onChange={(v) => updateSetting('autoLockDelay', v)}
                                    color="amber"
                                />
                            )}

                            {/* Privacy Notice */}
                            <div className="p-4 rounded-2xl bg-blue-500/10 border border-blue-500/30">
                                <div className="flex items-start gap-3">
                                    <Shield size={18} className="text-blue-400 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="text-sm font-semibold text-blue-400 mb-1">Privacy First</p>
                                        <p className="text-xs text-blue-300/80 leading-relaxed">
                                            Your biometric data never leaves your device. We only store encrypted authentication tokens that cannot be used to recreate your biometrics.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Performance Tab */}
                    {activeTab === 'performance' && (
                        <>
                            <SectionHeader icon={<Gauge />} title="Frame Rate" color="emerald" />

                            <SettingSelect
                                icon={<Activity size={18} />}
                                title="Target FPS"
                                description="Higher = smoother but uses more battery"
                                value={settings.targetFPS}
                                options={[
                                    { value: 30, label: '30 FPS', icon: <Battery size={12} /> },
                                    { value: 60, label: '60 FPS', icon: <Gauge size={12} /> },
                                    { value: 120, label: '120 FPS', icon: <Zap size={12} /> },
                                ]}
                                onChange={(v) => updateSetting('targetFPS', v)}
                                color="emerald"
                            />

                            <SectionHeader icon={<Cpu />} title="Rendering" color="cyan" />

                            <SettingToggle
                                icon={<Zap size={18} />}
                                title="Hardware Acceleration"
                                description="Use GPU for better performance"
                                enabled={settings.enableHardwareAcceleration}
                                onChange={(v) => updateSetting('enableHardwareAcceleration', v)}
                                color="cyan"
                            />

                            <SettingToggle
                                icon={<Sparkles size={18} />}
                                title="GPU Compositing"
                                description="Smooth animations with GPU"
                                enabled={settings.enableGPURendering}
                                onChange={(v) => updateSetting('enableGPURendering', v)}
                                color="cyan"
                            />

                            <SettingToggle
                                icon={<Eye size={18} />}
                                title="Reduced Animations"
                                description="Minimize animations for speed"
                                enabled={settings.reducedAnimations}
                                onChange={(v) => updateSetting('reducedAnimations', v)}
                            />

                            <SectionHeader icon={<Battery />} title="Power" color="green" />

                            <SettingToggle
                                icon={<Battery size={18} />}
                                title="Battery Optimization"
                                description="Reduce activity when on battery"
                                enabled={settings.batteryOptimization}
                                onChange={(v) => updateSetting('batteryOptimization', v)}
                                color="green"
                            />

                            <SettingToggle
                                icon={<Zap size={18} />}
                                title="Low Power Mode"
                                description="Extend battery with reduced features"
                                enabled={settings.lowPowerMode}
                                onChange={(v) => updateSetting('lowPowerMode', v)}
                                color="green"
                            />
                        </>
                    )}

                    {/* Notifications Tab */}
                    {activeTab === 'notifications' && (
                        <>
                            <SettingToggle
                                icon={<Bell size={18} />}
                                title="Push Notifications"
                                description="Receive push notifications"
                                enabled={settings.enableNotifications}
                                onChange={(v) => updateSetting('enableNotifications', v)}
                            />

                            {settings.enableNotifications && (
                                <>
                                    <SectionHeader icon={<MessageSquare />} title="Notification Types" color="blue" />

                                    <SettingToggle
                                        icon={<MessageSquare size={18} />}
                                        title="AI Responses"
                                        description="Notify when AI sends a response"
                                        enabled={settings.notifyOnNewResponse}
                                        onChange={(v) => updateSetting('notifyOnNewResponse', v)}
                                        color="blue"
                                    />

                                    <SettingToggle
                                        icon={<RefreshCw size={18} />}
                                        title="Token Refresh"
                                        description="Notify when tokens are refreshed"
                                        enabled={settings.notifyOnTokenRefresh}
                                        onChange={(v) => updateSetting('notifyOnTokenRefresh', v)}
                                        color="green"
                                    />

                                    <SettingToggle
                                        icon={<Trophy size={18} />}
                                        title="Achievements"
                                        description="Celebrate milestones"
                                        enabled={settings.notifyOnAchievements}
                                        onChange={(v) => updateSetting('notifyOnAchievements', v)}
                                        color="amber"
                                    />

                                    <SettingToggle
                                        icon={<Gift size={18} />}
                                        title="Luca's Quests"
                                        description="New quests available"
                                        enabled={settings.notifyOnQuests}
                                        onChange={(v) => updateSetting('notifyOnQuests', v)}
                                        color="pink"
                                    />

                                    <SettingToggle
                                        icon={<Volume2 size={18} />}
                                        title="Notification Sound"
                                        description="Play sound for notifications"
                                        enabled={settings.notificationSound}
                                        onChange={(v) => updateSetting('notificationSound', v)}
                                    />

                                    <SectionHeader icon={<Clock />} title="Study Reminder" color="purple" />

                                    <div className="p-4 rounded-2xl bg-slate-800/50 border border-slate-700/50">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-11 h-11 rounded-xl bg-purple-500/20 flex items-center justify-center">
                                                    <BookOpen size={18} className="text-purple-400" />
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-white text-sm">Daily Reminder</p>
                                                    <p className="text-xs text-slate-400">Get reminded to study</p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => updateSetting('studyReminderEnabled', !settings.studyReminderEnabled)}
                                                className={`
                                                    relative w-14 h-8 rounded-full transition-all duration-300
                                                    ${settings.studyReminderEnabled ? 'bg-purple-500' : 'bg-slate-600'}
                                                `}
                                            >
                                                <span className={`
                                                    absolute top-1 w-6 h-6 bg-white rounded-full shadow-lg transition-transform
                                                    ${settings.studyReminderEnabled ? 'translate-x-7' : 'translate-x-1'}
                                                `} />
                                            </button>
                                        </div>
                                        {settings.studyReminderEnabled && (
                                            <input
                                                type="time"
                                                value={settings.studyReminderTime}
                                                onChange={(e) => updateSetting('studyReminderTime', e.target.value)}
                                                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white text-sm focus:border-purple-500 focus:outline-none transition-colors"
                                            />
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {/* Storage Tab */}
                    {activeTab === 'storage' && (
                        <>
                            <SectionHeader icon={<Database />} title="Storage Usage" color="blue" />

                            {/* Storage Visualization */}
                            <div className="p-4 rounded-2xl bg-gradient-to-br from-blue-500/10 to-cyan-500/5 border border-blue-500/30">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                                            <HardDrive size={20} className="text-blue-400" />
                                        </div>
                                        <div>
                                            <p className="font-bold text-white">Cache Storage</p>
                                            <p className="text-xs text-slate-400">Currently using: {cacheSize}</p>
                                        </div>
                                    </div>
                                </div>

                                {storageUsage.total > 0 && (
                                    <div className="mb-4">
                                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-500"
                                                style={{ width: `${Math.min((storageUsage.used / storageUsage.total) * 100, 100)}%` }}
                                            />
                                        </div>
                                        <div className="flex justify-between mt-1.5 text-[10px] text-slate-500">
                                            <span>{storageUsage.used} MB used</span>
                                            <span>{storageUsage.total} MB available</span>
                                        </div>
                                    </div>
                                )}

                                <button
                                    onClick={handleClearCache}
                                    className="w-full py-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all"
                                >
                                    <Trash2 size={16} />
                                    Clear Cache
                                </button>
                            </div>

                            <SectionHeader icon={<Wifi />} title="Data & Offline" color="emerald" />

                            <SettingToggle
                                icon={<WifiOff size={18} />}
                                title="Offline Mode"
                                description="Access cached content without internet"
                                enabled={settings.enableOfflineMode}
                                onChange={(v) => updateSetting('enableOfflineMode', v)}
                                color="emerald"
                            />

                            <SettingToggle
                                icon={<Download size={18} />}
                                title="Auto-Download"
                                description="Download content for offline use on WiFi"
                                enabled={settings.autoDownloadContent}
                                onChange={(v) => updateSetting('autoDownloadContent', v)}
                                color="emerald"
                            />

                            <SettingToggle
                                icon={<Gauge size={18} />}
                                title="Data Saver"
                                description="Reduce data usage on mobile"
                                enabled={settings.dataSaverMode}
                                onChange={(v) => updateSetting('dataSaverMode', v)}
                                color="orange"
                            />
                        </>
                    )}

                    {/* Advanced Tab */}
                    {activeTab === 'advanced' && (
                        <>
                            <SectionHeader icon={<Brain />} title="AI Settings" color="purple" />

                            <SettingToggle
                                icon={<Waves size={18} />}
                                title="Streaming Responses"
                                description="See AI responses as they're generated"
                                enabled={settings.streamingEnabled}
                                onChange={(v) => updateSetting('streamingEnabled', v)}
                                color="purple"
                            />

                            <SettingToggle
                                icon={<Save size={18} />}
                                title="Auto-Save Conversations"
                                description="Automatically save chat history"
                                enabled={settings.autoSaveConversations}
                                onChange={(v) => updateSetting('autoSaveConversations', v)}
                                color="purple"
                            />

                            <SectionHeader icon={<Activity />} title="Debugging" color="red" />

                            <SettingToggle
                                icon={<Timer size={18} />}
                                title="FPS Counter"
                                description="Show real-time frame rate"
                                enabled={settings.showFPSCounter}
                                onChange={(v) => updateSetting('showFPSCounter', v)}
                                color="emerald"
                            />

                            <SettingToggle
                                icon={<AlertCircle size={18} />}
                                title="Debug Mode"
                                description="Show additional logging"
                                enabled={settings.debugMode}
                                onChange={(v) => updateSetting('debugMode', v)}
                                color="red"
                            />

                            {/* Memory Info */}
                            {memoryUsage !== '--' && (
                                <div className="p-4 rounded-2xl bg-slate-800/50 border border-slate-700/50">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                                            <Cpu size={18} className="text-cyan-400" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-white text-sm">Memory Usage</p>
                                            <p className="text-xs text-slate-400">{memoryUsage} JS Heap</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* App Info */}
                            <div className="p-4 rounded-2xl bg-gradient-to-br from-slate-800/80 to-slate-800/40 border border-slate-700/50">
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                                        <Info size={18} className="text-amber-400" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-semibold text-white mb-2">App Information</p>
                                        <div className="space-y-1 text-xs text-slate-400">
                                            <p>Version: <span className="text-white">4.0.0</span> (Build 40)</p>
                                            <p>Platform: <span className="text-white">Native APK</span></p>
                                            <p>Last Updated: <span className="text-white">{new Date().toLocaleDateString()}</span></p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Logout Button */}
                            {user && onLogout && (
                                <button
                                    onClick={() => {
                                        if (confirm('Are you sure you want to log out?')) {
                                            onLogout();
                                            onClose();
                                        }
                                    }}
                                    className="w-full py-3.5 rounded-xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 font-semibold text-sm flex items-center justify-center gap-2 transition-all"
                                >
                                    <LogOut size={18} />
                                    Log Out
                                </button>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 px-6 py-4 bg-gradient-to-t from-slate-900 via-slate-900/95 to-transparent border-t border-slate-800">
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className={`
                            w-full py-4 rounded-2xl font-bold text-base flex items-center justify-center gap-2.5 transition-all duration-300
                            ${showSaved
                                ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white'
                                : 'bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 text-white hover:shadow-xl hover:shadow-indigo-500/25 active:scale-[0.98]'
                            }
                        `}
                    >
                        {isSaving ? (
                            <Loader2 size={20} className="animate-spin" />
                        ) : showSaved ? (
                            <>
                                <CheckCircle size={20} />
                                Saved!
                            </>
                        ) : (
                            <>
                                <Save size={20} />
                                Save Settings
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default APKSettings;

// Helper functions
export function getAPKSettings(): APKSettingsConfig {
    try {
        const saved = localStorage.getItem('luca_apk_settings');
        if (saved) {
            return { ...DEFAULT_APK_SETTINGS, ...JSON.parse(saved) };
        }
    } catch (e) {
        console.error('[APKSettings] Failed to load settings');
    }
    return DEFAULT_APK_SETTINGS;
}

export function applyAPKSettings(): void {
    const settings = getAPKSettings();

    document.documentElement.style.setProperty(
        '--animation-duration-multiplier',
        settings.targetFPS === 30 ? '2' : settings.targetFPS === 60 ? '1' : '0.5'
    );

    if (settings.reducedAnimations) {
        document.documentElement.classList.add('reduce-motion');
    }

    if (settings.enableGPURendering) {
        document.documentElement.classList.add('gpu-accelerated');
    }
}
